import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Icon, type IconName } from './Icon'
import './ui.css'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'moss'
type Size = 'sm' | 'md' | 'lg'

interface Common {
  variant?: Variant
  size?: Size
  block?: boolean
  icon?: IconName
  iconEnd?: IconName
  loading?: boolean
  children?: ReactNode
  className?: string
}

export interface ButtonProps
  extends Common,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof Common | 'type'> {
  type?: 'button' | 'submit' | 'reset'
}

function classes({ variant = 'primary', size = 'md', block, className }: Common) {
  return clsx(
    'btn',
    'btn--' + variant,
    size !== 'md' && 'btn--' + size,
    block && 'btn--block',
    className,
  )
}

export function Button({
  variant,
  size,
  block,
  icon,
  iconEnd,
  loading,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classes({ variant, size, block, className })}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className="lens-spin" aria-hidden="true">
          <Icon name="lens" size={18} />
        </span>
      ) : (
        icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />
      )}
      {children}
      {iconEnd && !loading && <Icon name={iconEnd} size={size === 'sm' ? 16 : 18} />}
    </button>
  )
}

export function ButtonLink({
  to,
  variant,
  size,
  block,
  icon,
  iconEnd,
  children,
  className,
}: Common & { to: string }) {
  return (
    <Link to={to} className={classes({ variant, size, block, className })}>
      {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />}
      {children}
      {iconEnd && <Icon name={iconEnd} size={size === 'sm' ? 16 : 18} />}
    </Link>
  )
}

export function IconButton({
  name,
  label,
  onPhoto,
  size = 22,
  className,
  ...rest
}: {
  name: IconName
  label: string
  onPhoto?: boolean
  size?: number
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'name'>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx('icon-btn', onPhoto && 'icon-btn--on-photo', className)}
      {...rest}
    >
      <Icon name={name} size={size} />
    </button>
  )
}
